"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import {
  Home,
  Calendar,
  MapPin,
  Ticket,
  BarChart3,
  ClipboardList,
  Menu,
  Plus,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import { usePermissions } from "@/contexts/PermissionContext";
import { Permission } from "@/types/roles";
import { detectEventId, isActive, ROLE_LEVEL } from "./nav-model";
import { cn } from "@/lib/utils";
import type { Locale } from "@/i18n-config";
import type { Dictionary } from "@/types/dictionary";

interface BottomNavProps {
  lang: Locale;
  dict: Dictionary;
  onMore: () => void;
}

interface Tab {
  key: string;
  href: string;
  icon: LucideIcon;
  label: string;
  permission?: Permission;
  minRole?: string;
}

interface FabDescriptor {
  icon: LucideIcon;
  ariaLabel: string;
  permission: Permission;
  href?: string;
  event?: string; // CustomEvent name to dispatch
}

export function resolveFab(
  mode: "global" | "event",
  pathname: string,
  lang: string,
): FabDescriptor | null {
  const p = pathname.replace(new RegExp(`^/${lang}`), "");
  const segments = p.split("/").filter(Boolean);
  if (mode === "event") {
    // segments = ["events", eventId, <sub-page>, <detail id?>]
    // Bir alt sayfanin detayindaysak (ornegin tek bir davetli listesi) o
    // sayfanin kendi aksiyonlari var; listenin FAB'i burada hem ustu kapatiyor
    // hem de dinleyicisi mount olmadigi icin hicbir sey yapmiyor.
    if (segments.length > 3) return null;
    switch (segments[2]) {
      case "guest-lists":
        return {
          icon: Plus,
          ariaLabel: "Yeni Liste",
          permission: Permission.CREATE_GUEST_LIST,
          event: "openGuestListModal",
        };
      case "invitations":
        return {
          icon: Plus,
          ariaLabel: "Davetiye Oluştur",
          permission: Permission.CREATE_INVITATION,
          event: "openInvitationModal",
        };
      case "statistics":
      case "log":
      case "settings":
        return null;
      default:
        return {
          icon: UserPlus,
          ariaLabel: "Davetli Ekle",
          permission: Permission.CREATE_GUEST,
          event: "openGuestModal",
        };
    }
  }
  // Create/edit forms are already the action — nothing to launch from the nav.
  const last = segments[segments.length - 1];
  if (last === "new" || last === "edit") return null;
  if (p.startsWith("/venues")) {
    // Inside a venue the useful action is staffing it, not creating another one.
    if (segments.length > 1) {
      return {
        icon: UserPlus,
        ariaLabel: "Kişi Ata",
        permission: Permission.MANAGE_VENUE_PERMISSIONS,
        event: "openVenueAssign",
      };
    }
    return {
      icon: Plus,
      ariaLabel: "Yeni Mekan",
      permission: Permission.CREATE_VENUE,
      event: "openVenueModal",
    };
  }
  if (p.startsWith("/roles")) {
    return {
      icon: Plus,
      ariaLabel: "Yeni Rol",
      permission: Permission.CREATE_USER,
      href: `/${lang}/roles/new`,
    };
  }
  if (p.startsWith("/users")) {
    // A single user's detail page has no create-shaped action.
    if (segments.length > 1) return null;
    return {
      icon: UserPlus,
      ariaLabel: "Yeni Kullanıcı",
      permission: Permission.CREATE_USER,
      event: "openUserModal",
    };
  }
  return {
    icon: Plus,
    ariaLabel: "Yeni Etkinlik",
    permission: Permission.CREATE_EVENT,
    href: `/${lang}/events/new`,
  };
}

export function BottomNav({ lang, dict, onMore }: BottomNavProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { hasPermission, user, isSystemAdmin } = usePermissions();
  const roleLevel = isSystemAdmin
    ? ROLE_LEVEL.SYSTEM_ADMIN
    : (ROLE_LEVEL[user?.role ?? ""] ?? 0);

  const eventId = detectEventId(pathname, lang);
  const mode: "global" | "event" = eventId ? "event" : "global";
  const b = dict?.layout?.bottomNav as Record<string, string> | undefined;

  const left: Tab[] =
    mode === "event"
      ? [
          {
            key: "guests",
            href: `/events/${eventId}/guests`,
            icon: Ticket,
            label: "Davetliler",
          },
          {
            key: "statistics",
            href: `/events/${eventId}/statistics`,
            icon: BarChart3,
            label: "İstatistik",
          },
        ]
      : [
          {
            key: "home",
            href: "/dashboard",
            icon: Home,
            label: b?.home ?? "Anasayfa",
            minRole: "MANAGER",
          },
          {
            key: "events",
            href: "/events",
            icon: Calendar,
            label: b?.events ?? "Etkinlikler",
            permission: Permission.VIEW_EVENTS,
          },
        ];

  const right: Tab[] =
    mode === "event"
      ? [
          {
            key: "guestLists",
            href: `/events/${eventId}/guest-lists`,
            icon: ClipboardList,
            label: "Listeler",
          },
        ]
      : [
          {
            key: "venues",
            href: "/venues",
            icon: MapPin,
            label: b?.venues ?? "Mekanlar",
            permission: Permission.VIEW_VENUES,
          },
        ];

  const visible = (tabs: Tab[]) =>
    tabs.filter(
      (t) =>
        (!t.permission || hasPermission(t.permission)) &&
        (!t.minRole || roleLevel >= (ROLE_LEVEL[t.minRole] ?? 0)),
    );

  const fab = resolveFab(mode, pathname, lang);
  const fabAllowed = fab != null && hasPermission(fab.permission);
  const FabIcon = fab?.icon;
  const moreLabel = b?.more ?? "Menü";

  const renderTab = (t: Tab) => {
    const href = `/${lang}${t.href}`;
    const active = isActive(pathname, href);
    const Icon = t.icon;
    return (
      <Link
        key={t.key}
        href={href}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex flex-col items-center justify-center gap-1 h-full",
          "text-[10px] font-medium transition-colors focus-ring",
          active
            ? "text-brand"
            : "text-muted-foreground hover:text-brand-light",
        )}
      >
        <Icon className="w-[22px] h-[22px]" aria-hidden />
        <span>{t.label}</span>
      </Link>
    );
  };

  const onFab = () => {
    if (!fab) return;
    if (fab.href) router.push(fab.href);
    else if (fab.event) window.dispatchEvent(new CustomEvent(fab.event));
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-secondary border-t border-border"
      style={{
        height: "calc(68px + env(safe-area-inset-bottom, 16px))",
        paddingBottom: "env(safe-area-inset-bottom, 16px)",
      }}
    >
      <div className="grid grid-cols-5 items-stretch h-[68px]">
        {visible(left).map(renderTab)}

        <div
          className={cn(
            "relative flex justify-center",
            fabAllowed && fab && FabIcon ? "items-start" : "items-center",
          )}
        >
          {/* No action for this route — the bare mark sits inline with the tab icons */}
          {!(fabAllowed && fab && FabIcon) && (
            <Image
              src="/venuer-icon.png"
              alt=""
              width={38}
              height={38}
              className="h-[38px] w-[38px] opacity-50"
              aria-hidden
            />
          )}
          {fabAllowed && fab && FabIcon && (
            <button
              type="button"
              onClick={onFab}
              aria-label={fab.ariaLabel}
              className="w-14 h-14 -mt-[22px] rounded-full bg-brand text-primary-foreground inline-flex items-center justify-center transition-[background,transform] hover:bg-brand-light active:bg-brand-dark active:translate-y-px focus-ring"
              style={{
                boxShadow:
                  "0 12px 32px -6px var(--color-brand-glow-60), 0 0 0 4px var(--color-bg-dark)",
              }}
            >
              <FabIcon
                className="w-[26px] h-[26px]"
                strokeWidth={2.3}
                aria-hidden
              />
            </button>
          )}
        </div>

        {visible(right).map(renderTab)}

        <button
          type="button"
          onClick={onMore}
          aria-label={moreLabel}
          className="flex flex-col items-center justify-center gap-1 h-full text-[10px] font-medium text-muted-foreground hover:text-brand-light transition-colors focus-ring"
        >
          <Menu className="w-[22px] h-[22px]" aria-hidden />
          <span>{moreLabel}</span>
        </button>
      </div>
    </nav>
  );
}

export { type BottomNavProps };
